import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { api } from "./api";
import logo from "../assets/logoVerde.svg";
import fondo from "../assets/esquina.jpg";
import ComputerIcon from "@mui/icons-material/Computer";
import { useTurnstile } from "../hooks/useTurnstile";

const Register = ({ setUser }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [codigo, setCodigo] = useState("");
  const [form, setForm] = useState({
    nombre: "",
    apellido: "",
    documento_tipo: "",
    documento: "",
    telefono: "",
    usuario: "",
    contraseña: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [emailError, setEmailError] = useState("");
  const [codigoError, setCodigoError] = useState("");
  const [formErrors, setFormErrors] = useState({});

  const navigate = useNavigate();

  // -------------------------------
  // CAPTCHA usando useTurnstile hook
  // -------------------------------
  const {
    containerRef,
    token: captchaToken,
    loading: captchaLoading,
    failover: isCaptchaError,
    reset: resetCaptcha,
  } = useTurnstile({
    sitekey: "0x4AAAAAACE2Jp6L7wKno3E8",
    theme: "light",
  });

  const showMessage = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // ------------------------------------------------------
  // Paso 1: Enviar email con captcha integrado
  // ------------------------------------------------------
  const handleSendEmail = async () => {
    setLoading(true);
    setEmailError("");

    if (!email) {
      setEmailError("El email es obligatorio");
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Por favor ingresa un email válido");
      setLoading(false);
      return;
    }

    // Si el captcha falló, permitimos bypass
    const captchaBypass = isCaptchaError;

    try {
      const res = await api.auth.signupEmail(email, captchaToken, captchaBypass);

      if (res.error) {
        setEmailError(res.error);
        return;
      }

      showMessage(res.message || "Código enviado!", "success");
      setStep(2);

      // Resetear captcha solo si no fue bypass
      if (!captchaBypass) {
        resetCaptcha();
      }
    } catch (err) {
       resetCaptcha();
      setEmailError(err.error || err.error2 || "Error enviando email");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------
  // Paso 2: Verificar código enviado por email
  // ------------------------------------------------------
  const handleVerifyCode = async () => {
    setLoading(true);
    setCodigoError("");

    if (!codigo) {
      setCodigoError("Debes ingresar el código");
      setLoading(false);
      return;
    }

    try {
      const res = await api.auth.checkEmailVerifiedRegister(email, codigo);

      if (res.token) {
        const token = res.token.trim();
        localStorage.setItem("token", token);
        showMessage("Email verificado correctamente", "success");
        setStep(3);
      } else {
        setCodigoError("Código incorrecto");
      }
    } catch (err) {
      setCodigoError(err.error || "Error verificando email");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------
  // Paso 3: Completar formulario
  // ------------------------------------------------------
  const handleFormChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleFormSubmit = async () => {
    const errors = {};

    if (!form.nombre) errors.nombre = "El nombre es obligatorio";
    if (!form.apellido) errors.apellido = "El apellido es obligatorio";
    if (!form.documento_tipo) errors.documento_tipo = "El tipo de documento es obligatorio";
    if (!form.documento) errors.documento = "El documento es obligatorio";
    if (!form.telefono) errors.telefono = "El teléfono es obligatorio";
    if (!form.contraseña) errors.contraseña = "La contraseña es obligatoria";
    if (!form.confirmPassword) errors.confirmPassword = "Debes confirmar la contraseña";
    if (form.contraseña !== form.confirmPassword)
      errors.confirmPassword = "Las contraseñas no coinciden";

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.confirmPassword;

      const res = await api.usuario.registerUser(email, payload);
      setUser(res.user);
      localStorage.setItem("user", JSON.stringify(res.user));

      showMessage("Usuario registrado con éxito", "success");

      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (err) {

      showMessage(err.error || "Error registrando usuario", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderButtonContent = (text) =>
    loading ? <CircularProgress size={24} color="inherit" /> : text;

  const handleCancelConfirm = () => {
    setConfirmDialogOpen(false);
    navigate("/login");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        backgroundImage: `url(${fondo})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          bgcolor: "rgba(0,0,0,0.4)",
          zIndex: 1,
        },
      }}
    >
      <Box position="relative" zIndex={2} width="100%" maxWidth={450} sx={{ m: { xs: 2, sm: 3 } }}>
        <Box sx={{ backgroundColor: "white", p: 4, borderRadius: 2, boxShadow: 3, textAlign: "center" }}>

          <Box mb={1}>
            <img src={logo} alt="Logo" style={{ width: 90 }} />
          </Box>

          <Typography variant="subtitle1" mb={2} sx={{ fontWeight: "bold", fontSize: 15 }}>
            Cooperativa de Servicios Públicos, Sociales y Vivienda de Avellaneda Ltda.
          </Typography>

          <Box display="flex" justifyContent="center" mb={2}>
            <ComputerIcon sx={{ color: "#6aae3b", mr: 1, fontSize: 26 }} />
            <Typography variant="h5" sx={{ color: "#6aae3b", fontWeight: 600 }}>
              Oficina Virtual
            </Typography>
          </Box>

          <Box sx={{ width: 60, height: 2, bgcolor: "#6aae3b", mx: "auto", mb: 3 }} />

          {/* ----------------------------------- */}
          {/* PASO 1: EMAIL + CAPTCHA */}
          {/* ----------------------------------- */}
          {step === 1 && (
            <>
              <Typography variant="body1" mb={2}>
                Te enviaremos un código para validar tu identidad.
              </Typography>

              <TextField
                fullWidth
                size="small"
                label="Email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                error={!!emailError}
                helperText={emailError}
                sx={{ mb: 2 }}
              />

              {/* CAPTCHA con hook useTurnstile */}
              <Box display="flex" justifyContent="center" mb={2} minHeight={50}>
                {isCaptchaError ? (
                  <Alert severity="warning" sx={{ fontSize: 12 }}>
                    El servicio de verificación no está disponible.  
                    Continuarás sin validación de robot.
                  </Alert>
                ) : (
                  <div ref={containerRef} />
                )}
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleSendEmail}
                disabled={(!captchaToken && !isCaptchaError) || loading}
              >
                {renderButtonContent("Enviar código")}
              </Button>

              <Button fullWidth sx={{ mt: 1 }} onClick={() => navigate("/login")}>
                Volver
              </Button>
            </>
          )}

          {/* ----------------------------------- */}
          {/* PASO 2: CÓDIGO */}
          {/* ----------------------------------- */}
          {step === 2 && (
            <>
              <Typography variant="body1" mb={2}>
                Ingresá el código enviado a tu email
              </Typography>

              <TextField
                fullWidth
                size="small"
                label="Código"
                value={codigo}
                error={!!codigoError}
                helperText={codigoError}
                onChange={(e) => {
                  setCodigo(e.target.value);
                  setCodigoError("");
                }}
              />

              <Button
                fullWidth
                variant="contained"
                onClick={handleVerifyCode}
                sx={{ mt: 2 }}
              >
                {renderButtonContent("Verificar código")}
              </Button>

              <Button fullWidth sx={{ mt: 1 }} onClick={() => navigate("/login")}>
                Volver
              </Button>
            </>
          )}

          {/* ----------------------------------- */}
          {/* PASO 3: FORMULARIO FINAL */}
          {/* ----------------------------------- */}
          {step === 3 && (
            <>
              <Typography variant="body1" mb={2}>
                Completá tus datos
              </Typography>

              <TextField
                fullWidth
                size="small"
                label="Nombre"
                name="nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                error={!!formErrors.nombre}
                helperText={formErrors.nombre}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                size="small"
                label="Apellido"
                name="apellido"
                value={form.apellido}
                onChange={(e) => setForm({ ...form, apellido: e.target.value })}
                error={!!formErrors.apellido}
                helperText={formErrors.apellido}
                sx={{ mb: 2 }}
              />

              <Box display="flex" gap={2} mb={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo Doc</InputLabel>
                  <Select
                    name="documento_tipo"
                    value={form.documento_tipo}
                    onChange={(e) =>
                      setForm({ ...form, documento_tipo: e.target.value })
                    }
                    error={!!formErrors.documento_tipo}
                  >
                    <MenuItem value="DNI">DNI</MenuItem>
                    <MenuItem value="CLEX">CLEX</MenuItem>
                    <MenuItem value="CUIT">CUIT</MenuItem>
                    <MenuItem value="CUIL">CUIL</MenuItem>
                    <MenuItem value="PASO">PASO</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  label="Nro Documento"
                  name="documento"
                  value={form.documento}
                  onChange={(e) =>
                    setForm({ ...form, documento: Number(e.target.value) })
                  }
                  error={!!formErrors.documento}
                  helperText={formErrors.documento}
                />
              </Box>

              <TextField
                fullWidth
                size="small"
                label="Teléfono"
                name="telefono"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                error={!!formErrors.telefono}
                helperText={formErrors.telefono}
                sx={{ mb: 2 }}
              />

              <Box display="flex" gap={2} mb={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Contraseña"
                  name="contraseña"
                  value={form.contraseña}
                  onChange={(e) => setForm({ ...form, contraseña: e.target.value })}
                  error={!!formErrors.contraseña}
                  helperText={formErrors.contraseña}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Confirmar contraseña"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  error={!!formErrors.confirmPassword}
                  helperText={formErrors.confirmPassword}
                />
              </Box>

              <Box display="flex" gap={2}>
                <Button variant="outlined" onClick={() => setConfirmDialogOpen(true)}>
                  Cancelar
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleFormSubmit}
                >
                  {renderButtonContent("Registrarme")}
                </Button>
              </Box>
            </>
          )}
        </Box>

        {/* DIALOG CANCELAR */}
        <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
          <DialogTitle>Cancelar registro</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Si cancelas, no se guardarán tus datos. ¿Deseas continuar?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)}>No</Button>
            <Button onClick={handleCancelConfirm} autoFocus>
              Sí, cancelar
            </Button>
          </DialogActions>
        </Dialog>

        {/* SNACKBAR */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Register;
